import {
  GenerateImagePreviewDocument,
  type GenerateImagePreviewQuery,
  type GenerateImagePreviewQueryVariables,
  TextToImageDocument,
  type TextToImageMutation,
  type TextToImageMutationVariables,
} from "@/generated/graphql";
import { toastAiCredits } from "@/lib/utils/aiCredits";
import { Button, IconButton } from "@ai-teach/ui-web";
import { useLazyQuery, useMutation } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { FiImage, FiX } from "react-icons/fi";

interface TextToImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentFolderId: string | null;
}

type ImageSize = "square" | "landscape" | "portrait";
type ImageQuality = "standard" | "hd";

export function TextToImageModal({ isOpen, onClose, currentFolderId }: TextToImageModalProps) {
  // Input form state
  const [prompt, setPrompt] = useState("");
  const [filename, setFilename] = useState("");
  const [size, setSize] = useState<ImageSize>("square");
  const [quality, setQuality] = useState<ImageQuality>("standard");
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");

  // Preview state
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [revisedPrompt, setRevisedPrompt] = useState<string | null>(null);

  // Preview query
  const [generatePreview, { loading: isGenerating }] = useLazyQuery<
    GenerateImagePreviewQuery,
    GenerateImagePreviewQueryVariables
  >(GenerateImagePreviewDocument, {
    fetchPolicy: "network-only",
  });

  const [saveImage, { loading: isSaving }] = useMutation<
    TextToImageMutation,
    TextToImageMutationVariables
  >(TextToImageDocument, {
    refetchQueries: ["GetMediaHouseImages"],
    onCompleted: (data) => {
      toast.success(`Image generated successfully: ${data.textToImage.filename}`);
      onClose();
    },
    onError: (error) => {
      if (toastAiCredits(error, "textToImageSaveToast")) return;
      toast.error(`Failed to generate image: ${error.message}`);
    },
  });

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setPrompt("");
        setFilename("");
        setSize("square");
        setQuality("standard");
        setTags([]);
        setTagInput("");
        setImageUrl(null);
        setImageBase64(null);
        setRevisedPrompt(null);
      }, 300);
    }
  }, [isOpen]);

  // Clean up blob URL when component unmounts or image changes
  useEffect(() => {
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  const handleAddTag = () => {
    const trimmed = tagInput.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
      setTagInput("");
    }
  };

  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleGeneratePreview = async () => {
    if (!prompt.trim()) {
      toast.error("Please enter a prompt");
      return;
    }

    try {
      const { data, error } = await generatePreview({
        variables: {
          prompt,
          size,
          quality,
        },
      });

      // Rethrown as-is: its `extensions.code` is what identifies a spent AI balance downstream.
      if (error) {
        throw error;
      }

      if (!data?.generateImagePreview.success) {
        throw new Error(data?.generateImagePreview.error || "Image generation failed");
      }

      if (!data.generateImagePreview.imageBase64) {
        throw new Error("No image data returned");
      }

      // Convert base64 to blob URL for display
      const base64Data = data.generateImagePreview.imageBase64;
      const binaryString = atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: "image/png" });
      const url = URL.createObjectURL(blob);

      // Clean up old URL if exists
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }

      setImageBase64(base64Data);
      setImageUrl(url);
      setRevisedPrompt(data.generateImagePreview.revisedPrompt || null);
      toast.success("Image preview generated!");
    } catch (error) {
      if (toastAiCredits(error, "imagePreviewToast")) return;
      toast.error(error instanceof Error ? error.message : "Failed to generate image");
    }
  };

  const handleSave = async () => {
    if (!imageBase64) {
      toast.error("Please generate image preview first");
      return;
    }

    if (!filename.trim()) {
      toast.error("Please enter a filename");
      return;
    }

    if (tags.length === 0) {
      toast.error("Please add at least one tag");
      return;
    }

    await saveImage({
      variables: {
        input: {
          imageData: imageBase64,
          filename,
          tags,
          folderId: currentFolderId || undefined,
        },
      },
    });
  };

  if (!isOpen) return null;

  return (
    <div
      className="!mt-0 fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={isGenerating || isSaving ? undefined : onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="text-to-image-title"
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-6 py-4">
          <div className="flex items-center gap-2">
            <FiImage className="h-5 w-5 text-green-600" />
            <h2 id="text-to-image-title" className="h6">
              Generate Image from Text
            </h2>
          </div>
          <IconButton
            onClick={onClose}
            variant="ghost"
            className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
            disabled={isGenerating || isSaving}
          >
            <FiX className="h-5 w-5" />
          </IconButton>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Image Preview (shows when generated) */}
          {imageUrl && (
            <section>
              <label className="mb-2 block label">Image preview</label>
              <div className="rounded-md border border-gray-200 p-3 bg-gray-50">
                <img src={imageUrl} alt="Generated preview" className="w-full h-auto rounded" />
                {revisedPrompt && (
                  <div className="mt-2 p-2 bg-primary-50 rounded text-xs">
                    <p className="font-medium text-primary-900">Revised Prompt:</p>
                    <p className="text-primary-700 mt-1">{revisedPrompt}</p>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Filename Input (shows after preview) */}
          {imageUrl && (
            <section>
              <label htmlFor="filename" className="mb-2 block label">
                Filename <span className="text-red-500">*</span>
              </label>
              <input
                id="filename"
                type="text"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                placeholder="my-image-file"
                required
                disabled={isGenerating || isSaving}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-100"
              />
              <p className="mt-1 text-xs text-gray-500">
                The file will be saved as {filename || "filename"}.png
              </p>
            </section>
          )}

          {/* Prompt Input */}
          <section>
            <label htmlFor="prompt" className="mb-2 block label">
              Prompt <span className="text-red-500">*</span>
            </label>
            <textarea
              id="prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe the image you want to generate..."
              rows={4}
              maxLength={4000}
              required
              disabled={isGenerating || isSaving}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-100"
            />
            <p className="mt-1 text-xs text-gray-500">{prompt.length}/4000 characters</p>
          </section>

          {/* Options Grid - Size, Quality */}
          <div className="grid grid-cols-2 gap-4">
            {/* Size Selector */}
            <section>
              <label className="mb-2 block label">
                Size <span className="text-red-500">*</span>
              </label>
              <div className="space-y-1">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="size"
                    value="square"
                    checked={size === "square"}
                    onChange={() => setSize("square")}
                    disabled={isGenerating || isSaving}
                    className="h-3.5 w-3.5 text-primary-600"
                  />
                  <span className="text-xs">Square</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="size"
                    value="landscape"
                    checked={size === "landscape"}
                    onChange={() => setSize("landscape")}
                    disabled={isGenerating || isSaving}
                    className="h-3.5 w-3.5 text-primary-600"
                  />
                  <span className="text-xs">Landscape</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="size"
                    value="portrait"
                    checked={size === "portrait"}
                    onChange={() => setSize("portrait")}
                    disabled={isGenerating || isSaving}
                    className="h-3.5 w-3.5 text-primary-600"
                  />
                  <span className="text-xs">Portrait</span>
                </label>
              </div>
            </section>

            {/* Quality Selector */}
            <section>
              <label className="mb-2 block label">
                Quality <span className="text-red-500">*</span>
              </label>
              <div className="space-y-1">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="quality"
                    value="standard"
                    checked={quality === "standard"}
                    onChange={() => setQuality("standard")}
                    disabled={isGenerating || isSaving}
                    className="h-3.5 w-3.5 text-primary-600"
                  />
                  <span className="text-xs">Standard</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="quality"
                    value="hd"
                    checked={quality === "hd"}
                    onChange={() => setQuality("hd")}
                    disabled={isGenerating || isSaving}
                    className="h-3.5 w-3.5 text-primary-600"
                  />
                  <span className="text-xs">HD</span>
                </label>
              </div>
            </section>
          </div>

          {/* Generate Preview Button */}
          <Button
            onClick={handleGeneratePreview}
            disabled={isGenerating || isSaving || !prompt.trim()}
            variant="primary"
            className="w-full bg-green-600 hover:bg-green-700"
          >
            {isGenerating && (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <title>Generating</title>
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {isGenerating ? "Generating..." : "Generate Image Preview"}
          </Button>

          {/* Tags Section (shows after preview) */}
          {imageUrl && (
            <section>
              <label htmlFor="tag-input" className="mb-2 block label">
                Tags <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <input
                  id="tag-input"
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Add a tag and press Enter"
                  disabled={isGenerating || isSaving}
                  className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-100"
                />
                <Button
                  onClick={handleAddTag}
                  disabled={isGenerating || isSaving || !tagInput.trim()}
                  variant="secondary"
                  size="md"
                >
                  Add
                </Button>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-sm text-primary-700"
                  >
                    {tag}
                    <IconButton
                      onClick={() => handleRemoveTag(tag)}
                      disabled={isGenerating || isSaving}
                      variant="ghost"
                      size="sm"
                      aria-label={`Remove tag ${tag}`}
                      className="h-4 w-4 p-0 hover:text-primary-900"
                    >
                      ×
                    </IconButton>
                  </span>
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {tags.length === 0
                  ? "Add at least one tag"
                  : `${tags.length} tag${tags.length > 1 ? "s" : ""} added`}
              </p>
            </section>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 border-t bg-white px-6 py-4 flex justify-end gap-3">
          <Button onClick={onClose} variant="secondary" disabled={isGenerating || isSaving}>
            Cancel
          </Button>
          {imageUrl && (
            <Button
              onClick={handleSave}
              disabled={isGenerating || isSaving || !filename.trim() || tags.length === 0}
              variant="primary"
            >
              {isSaving && (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <title>Saving</title>
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
              {isSaving ? "Saving..." : "Save to Library"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
